import { computed, onBeforeMount } from 'vue';
import ApiClient from '@/ApiClient';
import { isScopedAdmin } from '@/guards';
import { useStateComposable } from '@/state/composables';

const { computedState, state } = useStateComposable({
  switchAccounts: [],
  requested: false,
});

const accountNames = computed(() => state.switchAccounts.map(a => a.name));

// The endpoint excludes the active company, so a non-empty list means the admin
// has at least one other company to be confused with.
const hasMultipleCompanies = computed(() => state.switchAccounts.length > 0);

// Whether to surface the active company to the user (B2B-54).
//
// For most admins hasMultipleCompanies is the honest answer: one company means
// nothing to disambiguate. But /auth/switch/company is company-admin gated, so
// for a recognition-only admin the list is ALWAYS empty and we genuinely cannot
// tell how many companies they have — there is no other signal (/auth/me and the
// JWT both carry only the active company, and the login payload that does know
// is gone by the time any page renders). paydays-api#27514 would have fixed
// that and is closed for good.
//
// So fail open for them rather than closed. They are the cohort this ticket was
// raised for, and the costs are asymmetric: a redundant pill naming the one
// company they administer is noise, whereas no pill for a two-company admin is
// the original bug — awards created against the wrong company.
const shouldShowCompanyIndicator = computed(() => hasMultipleCompanies.value || isScopedAdmin());

const getSwitchAccounts = async () => {
  state.requested = true;
  try {
    state.switchAccounts = (await ApiClient.getAuthSwitch())?.data || [];
  } catch (error) {
    // /auth/switch/company is company-admin gated, so recognition-only admins
    // get a 403 here. An empty list is the right answer for the switcher itself
    // (there is nothing it can offer them); shouldShowCompanyIndicator is what
    // compensates for the fact that empty does not mean "one company" for them.
    state.switchAccounts = [];
  }
};

export default () => {
  onBeforeMount(() => {
    // Shared across every consumer: AppPage used to re-fetch this on each page
    // mount, which is what the comment in ApiClient.getAuthSwitch refers to.
    if (!state.requested) {
      getSwitchAccounts();
    }
  });

  return {
    ...computedState,
    accountNames,
    hasMultipleCompanies,
    shouldShowCompanyIndicator,
  };
};
